import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { photographyServices } from '@/mocks/services'
import { buildWhatsAppLink } from '@/utils/whatsapp'

export default function ServicesSection() {
  const { t } = useTranslation()

  return (
    <section id="services" className="w-full bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-500" />
              <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                {t('services.label')}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
              {t('services.title')}
            </h2>
          </div>
          <p className="text-base text-foreground-600 leading-relaxed max-w-md">{t('services.intro')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {photographyServices.map((service) => {
            const features = t(`services.items.${service.id}.features`, { returnObjects: true }) as string[]
            const serviceName = t(`services.items.${service.id}.title`)
            const enquireLink = buildWhatsAppLink(t('common.enquireMessage', { topic: serviceName }))
            return (
              <article
                key={service.id}
                className="group rounded-lg border border-background-200 bg-background-50 p-6 md:p-7 flex flex-col transition-colors hover:border-primary-300"
              >
                <span className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-100 text-primary-700 mb-5 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${service.icon} text-2xl`} />
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-2">
                  {serviceName}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  {t(`services.items.${service.id}.desc`)}
                </p>
                <ul className="flex flex-wrap gap-2">
                  {features.map((feature) => (
                    <li
                      key={feature}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap"
                    >
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-6 flex flex-wrap items-center gap-3">
                  <a
                    href={enquireLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-whatsapp-line text-base" />
                    {t('common.enquire')}
                  </a>
                  {service.id === 'wedding' && (
                    <Link
                      to="/wedding"
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-300 text-foreground-800 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-700 transition-colors"
                    >
                      {t('common.viewDetails')}
                      <i className="ri-arrow-right-line text-base" />
                    </Link>
                  )}
                </div>
              </article>
            )
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 h-12 px-8 rounded-md bg-foreground-950 text-background-50 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
          >
            <i className="ri-calendar-check-line text-lg" />
            {t('common.discussEvent')}
          </Link>
        </div>
      </div>
    </section>
  )
}